import { Button, cn, focusHaloFlush, Input, Label } from "@pax/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { type SubmitEvent, useState } from "react";
import { useAccountDeletion } from "@/features/auth/account-deletion";
import { type Session, sessionKey, signOut } from "@/features/auth/queries";
import { inviteCodeFrom } from "./invites";

/**
 * The whole window while the signed-in account is on the waitlist: no sidebar, panels, or footer.
 * Pasting an invite link opens it, and checking again reloads the session, which opens Pax once an
 * allowed email has let the account in.
 */
export function WaitlistScreen({ session }: { session: Session }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const { openAccountDeletion } = useAccountDeletion();
  const [pasted, setPasted] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [checking, setChecking] = useState(false);

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const code = inviteCodeFrom(pasted);
    if (!code) {
      setInvalid(true);
      return;
    }
    void navigate({ to: "/invites/$code", params: { code } });
  }

  async function handleCheckAgain() {
    setChecking(true);
    try {
      await queryClient.invalidateQueries({ queryKey: sessionKey, exact: true });
    } finally {
      setChecking(false);
    }
  }

  async function handleSignOut() {
    try {
      await signOut(queryClient);
    } finally {
      await navigate({ to: "/login" });
      await router.invalidate();
    }
  }

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center overflow-y-auto bg-background px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold">You're on the waitlist</h1>
          <p className="text-sm text-muted-foreground">
            Pax is invite-only while it's in beta. You're signed in as{" "}
            <span className="text-foreground">{session.user.email}</span>, and Pax opens here as
            soon as you're let in.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <Label htmlFor="waitlist-invite">Have an invite?</Label>
          <div className="flex gap-2">
            <Input
              id="waitlist-invite"
              value={pasted}
              onChange={event => {
                setPasted(event.target.value);
                setInvalid(false);
              }}
              placeholder="Paste the invite link"
              autoComplete="off"
              aria-invalid={invalid}
              aria-describedby={invalid ? "waitlist-invite-error" : undefined}
            />
            <Button type="submit" disabled={pasted.trim() === ""}>
              Use
            </Button>
          </div>
          {invalid ? (
            <p id="waitlist-invite-error" className="text-xs text-destructive">
              That doesn't look like an invite link.
            </p>
          ) : null}
        </form>

        <Button variant="outline" disabled={checking} onClick={() => void handleCheckAgain()}>
          {checking ? "Checking…" : "Check again"}
        </Button>

        <div className="flex justify-center gap-4">
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className={cn(
              "rounded text-xs text-muted-foreground hover:text-foreground",
              focusHaloFlush,
            )}
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={openAccountDeletion}
            className={cn(
              "rounded text-xs text-muted-foreground hover:text-foreground",
              focusHaloFlush,
            )}
          >
            Delete account
          </button>
        </div>
      </div>
    </div>
  );
}
